import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Container, Row } from "reactstrap";
import {
  Typography,
  Chip,
  Card,
  CardHeader,
  CardContent,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStamp } from "@fortawesome/pro-duotone-svg-icons";
import ErrorBoundary from "../../ErrorBoundry";
import ApprovalModal from "../../CommonComponents/ApprovalModal/ApprovalModal";
import {
  getPendingPositionMappings,
  decidePositionMapping,
} from "../../store/positionMapping/actions";

const renderValue = (value) =>
  value !== null && value !== undefined && value !== "" ? value : "N/A";

function PositionMappingApprovals() {
  const dispatch = useDispatch();
  const { pendingList, loadingPending } = useSelector(
    (state) => state.PositionMappingReducer
  );
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    dispatch(getPendingPositionMappings());
  }, [dispatch]);

  const handleDecision = ({ option, remarks }) => {
    dispatch(
      decidePositionMapping(selectedRequest.id, {
        decision: option === "Approved" ? "Approved" : "Rejected",
        remarks,
      })
    );
    setSelectedRequest(null);
  };

  return (
    <ErrorBoundary>
      <Fragment>
        <Container fluid={true}>
          <Row className="bg-primary rounded py-2 mb-3 sticky-top">
            <Col className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faStamp} size="xl" color="white" />
              <Typography variant="h6" style={{ color: "white" }}>
                Position Mapping Approvals
              </Typography>
            </Col>
          </Row>

          <Card>
            <CardHeader
              title="Pending HR position <-> Payroll job classification mappings"
              subheader="Approve to write the position back onto the payroll job classification record, or reject with a reason."
            />
            <CardContent>
              {loadingPending ? (
                <Typography variant="body2" className="p-3 text-center">
                  Loading...
                </Typography>
              ) : pendingList.length === 0 ? (
                <Typography variant="body2" className="p-3 text-center">
                  No pending mapping requests.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>HR Position</TableCell>
                      <TableCell>Payroll Job Classification</TableCell>
                      <TableCell>Proposed On</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingList.map((row) => {
                      const isNewClassification = row.requestType === "NewClassification";
                      return (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Chip
                              size="small"
                              label={isNewClassification ? "Needs New Classification" : "Mapping"}
                              color={isNewClassification ? "warning" : "default"}
                            />
                          </TableCell>
                          <TableCell>
                            {renderValue(row.npositionNo)} - {renderValue(row.hrPositionTitle)}
                          </TableCell>
                          <TableCell>
                            {isNewClassification
                              ? "— (none yet)"
                              : `${renderValue(row.jobClassificationCode)} - ${renderValue(
                                  row.payrollPositionTitle
                                )}`}
                          </TableCell>
                          <TableCell>
                            {row.proposedDate ? new Date(row.proposedDate).toLocaleString() : "N/A"}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => setSelectedRequest(row)}
                            >
                              Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Container>

        <ApprovalModal
          open={!!selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onSubmit={handleDecision}
          title={
            selectedRequest?.requestType === "NewClassification"
              ? "New Job Classification Request"
              : "Position Mapping Approval"
          }
          approveLabel={
            selectedRequest?.requestType === "NewClassification"
              ? "Acknowledge - Will Create Classification"
              : "Approve and Map Position"
          }
          returnLabel={
            selectedRequest?.requestType === "NewClassification"
              ? "Reject Request"
              : "Reject Mapping Proposal"
          }
        />
      </Fragment>
    </ErrorBoundary>
  );
}

export default PositionMappingApprovals;
